import React, { createContext, Fragment, useCallback, useEffect, useMemo, useRef } from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { componentWithCondition } from '@atlaskit/platform-feature-flags-react/component-with-condition';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ForgeUIExtensionType } from '@atlassian/forge-ui-types';

import { type AppPerformanceProfilingEvent, getPerformanceMark } from '../performance-profiling';
import { getAppPerfCaptureConfig } from './getAppPerfCaptureConfig';
import { useAppPerfCaptureStrategy } from './useAppPerfCaptureStrategy';

export type PerformanceProfileTimeline = {
	events: AppPerformanceProfilingEvent[];
	listeners: Set<(event: AppPerformanceProfilingEvent) => void>;
};

export const PerformanceAnalyticsContext: React.Context<
	React.RefObject<PerformanceProfileTimeline>
> = createContext<React.RefObject<PerformanceProfileTimeline>>(null!);

export const PERFORMANCE_ANALYTICS_INTERVAL = 10_000;

const offsetElapsedTimestamps = (
	events: AppPerformanceProfilingEvent[],
	elapsedBaselineMark: number,
) => {
	return events.map((event) => {
		const evaluatedElapsedTime = event.elapsed - elapsedBaselineMark;
		event.elapsed = evaluatedElapsedTime;
		return event;
	});
};

type PeroformanceAnalyticsProviderProps = {
	appType: 'UIKit' | 'CustomUI';
	appVersion: string | undefined;
	children: React.ReactNode;
	extensionType: string;
	forgeEnvironment: ForgeUIExtensionType['environmentType'];
	profileKey: string;
};

export const PerformanceAnalyticsProvider = ({
	children,
	appType,
	forgeEnvironment,
	profileKey,
	extensionType,
	appVersion,
}: PeroformanceAnalyticsProviderProps): React.JSX.Element => {
	// Rely on refs to avoid re-rendering all children components when the state changes
	const performanceTimelineRef = useRef<PerformanceProfileTimeline>({
		events: [],
		listeners: new Set(),
	});
	const hasSent = useRef(false);
	const elapsedBaselineMark = useRef(0);
	// Store profile ID in ref to generate once per session and reuse
	const profileIdRef = useRef<string | null>(null);

	const captureConfig = getAppPerfCaptureConfig({ module: extensionType });
	const { enabled, capture } = useAppPerfCaptureStrategy({
		config: captureConfig,
		samplingKey: profileKey,
	});

	// Reset state when profileKey changes
	useEffect(() => {
		hasSent.current = false;
		performanceTimelineRef.current.events = [];
		elapsedBaselineMark.current = getPerformanceMark();
		profileIdRef.current = null; // Reset profile ID for new session
		// Use `profileKey` to reset all state when the extension or entryPoint changes to collect a new set of analytics
	}, [profileKey]);

	// Memoize static attributes to avoid recreating on every render
	const staticAttributes = useMemo(
		() => ({
			appType,
			forgeEnvironment,
			appVersion,
			eventVersion: captureConfig.version,
		}),
		[appType, forgeEnvironment, appVersion, captureConfig.version],
	);

	// Create stable callback for capturing analytics
	const captureAnalytics = useCallback(() => {
		// Generate profile ID once per session
		if (!profileIdRef.current) {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			profileIdRef.current = uuid();
		}

		capture({
			profileId: profileIdRef.current,
			events: offsetElapsedTimestamps(
				performanceTimelineRef.current.events,
				elapsedBaselineMark.current,
			),
			// Time difference since this event was fired and since page loaded
			navigationTimeStart: elapsedBaselineMark.current,
			...staticAttributes,
		});

		hasSent.current = true;
	}, [capture, staticAttributes]);

	// Send all collected performance related analytics in a single event
	useEffect(() => {
		if (!enabled || hasSent.current) {
			return;
		}

		const timeout = setTimeout(() => {
			captureAnalytics();
		}, PERFORMANCE_ANALYTICS_INTERVAL);

		return () => {
			clearTimeout(timeout);
		};
	}, [enabled, captureAnalytics]);

	return (
		<PerformanceAnalyticsContext.Provider value={performanceTimelineRef}>
			{children}
		</PerformanceAnalyticsContext.Provider>
	);
};

export const PerformanceAnalyticsProviderRollout: React.FC<
	PeroformanceAnalyticsProviderProps & {
		children?: React.ReactNode | undefined;
	}
> = componentWithCondition(
	() => fg('forge-ui-app-perf-tracking-enabled'),
	PerformanceAnalyticsProvider,
	Fragment,
);
